import React, { useState } from 'react';
import * as Yup from 'yup';
import { Formik, FormikErrors } from 'formik';
import { useTranslation } from 'react-i18next';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import InputFormField from 'components/v2/form/InputFormField';
import AuthService from 'services/AuthService';
import {
  DialogClearStylesButtonsFooter,
  DialogContent,
  DialogFooter,
  InputsContainer,
} from './DialogClearStyles';
import { toaster } from 'components/v2/toast';
import { Description } from 'components/v2/confirm-dialog/styled';
import { HelpBox, HelpBoxBoldText } from 'components/v2/help-boxes';
import VerificationCodeInput from 'components/controls/VerificationCodeInput';
import styled from 'styled-components';
import { UpdateUserEmailModel } from 'services/EpdClient';
import { useHistory } from 'react-router-dom';
import ResendTimerButton from './ResendTimerButton';

interface Props {
  onHide(): void;
}

const ChangeEmailDialogForm: React.FC<Props> = ({ onHide }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [step, setStep] = useState<1 | 2>(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<'passed' | 'failed' | null>(null);
  const [resendCooldownEndTimestamp, setResendCooldownEndTimestamp] = useState<number | null>(null);

  const validationSchema = Yup.object().shape({
    currentEmail: Yup.string().email(t('Invalid email')).required(t('account.validation.emailRequired')),
    newEmail: Yup.string()
      .email(t('Invalid email'))
      .notOneOf([Yup.ref('currentEmail')], t('account.validation.newEmailNotSame'))
      .required(t('account.validation.emailRequired')),
    verificationCode: Yup.string()
      .matches(/^\d{6}$/, t('Verification code is required'))
  });

  const user = AuthService.getUser();
  const initialValues: UpdateUserEmailModel = {
    currentEmail: user.email || '',
    newEmail: '',
    verificationCode: '',
  };

  const handleSubmit = async (values: typeof initialValues) => {
    setIsSubmitting(true);
    try {
      await AuthService.updateUserEmail(values);
      AuthService.logout(history);
    } catch (error: any) {
      const serverError = error?.response ? JSON.parse(error.response) : {};
      if (serverError?.detail?.includes('EXISTING_EMAIL')) {
        toaster({
          severity: 'error',
          summary: 'This email address is already in use.',
          details: 'Please choose a different one.',
        });
      } else {
        toaster({
          severity: 'error',
          summary: 'Unable to update account information due to an unexpected error.',
          details: 'Please try again later or contact support.',
        });
      }
    } finally {
      onHide();
    }
  };

  const trySendVerificationCode = async (newEmail: string): Promise<boolean> => {
    try {
      await AuthService.sendEmailUpdateVerificationCode(newEmail);
      return true;
    } catch (error) {
      toaster({
        severity: 'error',
        summary: `We're having trouble sending the email right now.`,
        details: 'Please try again later or contact support.',
      });
      return false;
    }
  };

  const handleContinue = async (
    values: typeof initialValues,
    setFieldError: (field: string, message: string) => void,
    validateForm: () => Promise<FormikErrors<typeof initialValues>>,
    setTouched: (fields: any, touched?: boolean) => void,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    const errors = await validateForm();
    if (Object.keys(errors).length > 0) {
      setTouched({ newEmail: true, currentEmail: true }, true);
      return;
    }
    setIsSubmitting(true);
    try {
      await AuthService.validateEmail(values.newEmail);
      setVerificationStatus(null);
      setFieldValue('verificationCode', '');

      const codeSent = await trySendVerificationCode(values.newEmail);
      if (codeSent) {
        setStep(2);
        setResendCooldownEndTimestamp(Date.now() + 30000);
      } else {
        onHide();
      }
    } catch (error: any) {
      const serverError = error?.response ? JSON.parse(error.response) : {};
      if (serverError?.detail?.includes('EXISTING_EMAIL')) {
        setFieldError('newEmail', 'This email address is already in use. Please choose a different one.');
      } else {
        toaster({
          severity: 'error',
          summary: 'Failed to validate email.',
          details: 'Please try again later.',
        });
        onHide();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const goToPreviousStep = () => {
    setStep(1);
    setVerificationStatus(null);
  };

  const validateVerificationCode = async (code: string) => {
    setIsSubmitting(true);
    try {
      const isValidCode = await AuthService.validateEmailUpdateVerificationCode(code);
      setVerificationStatus(isValidCode ? 'passed' : 'failed');
    } catch (error) {
      setVerificationStatus(null);
      toaster({
        severity: 'error',
        summary: `We're having trouble validating confirmation code.`,
        details: 'Please try again later or contact support.',
      });
    }
    setIsSubmitting(false);
  };

  const resendVerificationCode = async (
    newEmail: string,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (resendCooldownEndTimestamp && Date.now() < resendCooldownEndTimestamp) {
      return;
    }
    setVerificationStatus(null);
    setFieldValue('verificationCode', '');

    const codeSent = await trySendVerificationCode(newEmail);
    if (!codeSent) {
      onHide();
      return;
    }
    setResendCooldownEndTimestamp(Date.now() + 30000);
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={validationSchema} enableReinitialize>
      {({ handleSubmit: formikSubmit, isValid, errors, touched, values, setFieldError, setTouched, validateForm, setFieldValue }) => {
        const continueButtonDisabled =
          (!isValid && Object.keys(errors).some(errKey => touched[errKey as keyof UpdateUserEmailModel])) || isSubmitting;
        const saveButtonDisabled = !isValid || isSubmitting || verificationStatus === 'failed' || values.verificationCode.length !== 6;

        return (
          <>
            {step === 1 && (
              <DialogContent as="div">
                <Description>
                  <HelpBox>
                    You are about to update the email address linked to your user account. After the change, the new email
                    will be used for login. Your current account information and access will remain unchanged.
                  </HelpBox>
                </Description>
                <InputsContainer style={{ marginTop: '30px' }}>
                  <InputFormField label="Current email" disabled name="currentEmail" autoComplete="off" />
                  <InputFormField label="New email" name="newEmail" errorPositioning="absolute" autoComplete="off" />
                </InputsContainer>
                <DialogFooter>
                  <DialogClearStylesButtonsFooter>
                    <SecondaryButton type="button" label="Cancel" onClick={onHide} />
                    <PrimaryButton
                      type="button"
                      label="Continue"
                      onClick={() => {
                        handleContinue(values, setFieldError, validateForm, setTouched, setFieldValue);
                      }}
                      disabled={continueButtonDisabled}
                    />
                  </DialogClearStylesButtonsFooter>
                </DialogFooter>
              </DialogContent>
            )}
            {step === 2 && (
              <DialogContent as="div">
                <Description>
                  {verificationStatus === null && (
                    <HelpBox>
                      <HelBoxEvenBolderText isError={false}>Please check your email.</HelBoxEvenBolderText>
                      <div>{`We've sent a code to ${values.newEmail}`}</div>
                    </HelpBox>
                  )}
                  {verificationStatus === 'passed' && (
                    <HelpBoxSuccess>
                      <HelBoxEvenBolderText isError={false}>New email is verified!</HelBoxEvenBolderText>
                      <div>Save the changes and log in to the system again using the new email.</div>
                    </HelpBoxSuccess>
                  )}
                  {verificationStatus === 'failed' && (
                    <HelpBoxError>
                      <HelBoxEvenBolderText isError={true}>Invalid verification code.</HelBoxEvenBolderText>
                      <div>Please try again or click the link below to request a new code.</div>
                    </HelpBoxError>
                  )}
                </Description>
                <InputsContainer style={{ marginTop: '30px' }}>
                  <VerificationCodeInput
                    name="verificationCode"
                    length={6}
                    label="Enter verification code"
                    loading={isSubmitting}
                    onComplete={validateVerificationCode}
                  />
                </InputsContainer>
                <ResendDiv>
                  <span style={{ marginRight: '4px' }}>Didn't get a code?</span>
                  <ResendTimerButton
                    cooldownEndTime={resendCooldownEndTimestamp}
                    onResend={() => resendVerificationCode(values.newEmail, setFieldValue)}
                  />
                </ResendDiv>
                <DialogFooter>
                  <DialogClearStylesButtonsFooter>
                    <SecondaryButton type="button" label="Back" onClick={goToPreviousStep} />
                    <PrimaryButton
                      type="submit"
                      label="Save"
                      onClick={() => formikSubmit()}
                      disabled={saveButtonDisabled}
                    />
                  </DialogClearStylesButtonsFooter>
                </DialogFooter>
              </DialogContent>
            )}
          </>
        );
      }}
    </Formik>
  );
};

export default ChangeEmailDialogForm;

const HelBoxEvenBolderText = styled(HelpBoxBoldText) <{ isError?: boolean }>`
  font-weight: 700;
  color: ${({ isError: errorColor, theme }) => errorColor ? theme.colors.systemStatusError : 'inherit'};
`;

const HelpBoxError = styled(HelpBox)`
  background-color: ${(props) => props.theme.colors.statusLabelRedBg};
  color: ${(props) => props.theme.colors.systemStatusError};
`;

const HelpBoxSuccess = styled(HelpBox)`
  background-color: ${(props) => props.theme.colors.secondaryLightGreen30};
`;

const ResendDiv = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-family: Inter;
  margin-top: 10px;
`;
